/**
 * S2 — Campaign Approvals list (`/approvals`). A header count + a list of
 * approval cards. Re-approval cards carry a claret banner naming the material
 * change. Each card links to the detail screen where the three-layer confirm
 * flow lives. No bulk approve (each campaign confirmed individually).
 *
 * Spec: teivor-build-spec/08-ui-spec.md §S2. Role: `client` confirms.
 *
 * NOTE FOR ORCHESTRATOR: the `com` approval entity exposes `campaignId` +
 * `campaignSnapshotRef` only — not a human campaign name nor the readable
 * snapshot summary (ICP / sequence / identity / run mode) the spec describes.
 * We render the reference honestly; wire those fields server-side to enrich.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Skeleton,
} from '@teivor/shared-ui';
import { formatDateTime } from '@teivor/shared-utils';

import { PageContainer } from '../../../components/PageContainer';
import { useApprovalsQuery } from '../queries';
import type { CampaignApproval } from '../types';

import {
  campaignTitle,
  reapprovalReasonLabel,
  statusBadge,
  type ApprovalStatusBadge,
} from './approval-shared';

function useLocale(): 'cs-CZ' | 'en-US' {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith('en') ? 'en-US' : 'cs-CZ';
}

function ApprovalCard({ approval, onOpen }: { approval: CampaignApproval; onOpen: () => void }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const badge: ApprovalStatusBadge = statusBadge(approval.status);
  const isReapproval = approval.status === 'awaiting' && approval.reapprovalReason !== null;

  return (
    <Card>
      {isReapproval ? (
        <div className="flex items-start gap-2 rounded-t-[var(--tvl-r3)] border-b border-[var(--tvl-accent)] bg-[var(--tvl-accent)] px-4 py-2 text-[13px] leading-[18px] text-[var(--tvl-on-accent)]">
          <span>
            {t('com.approvals.reapproval_banner', 'Změna kampaně — vyžaduje nové schválení / Campaign changed — re-confirmation required')}
            {approval.reapprovalReason
              ? ` · ${reapprovalReasonLabel(approval.reapprovalReason, t)}`
              : ''}
          </span>
        </div>
      ) : null}
      <CardContent className="flex flex-col gap-3 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-[16px] font-semibold leading-[22px] text-[var(--tvl-ink)]">
              {campaignTitle(approval, t)}
            </h2>
            <span
              className="text-[12px] leading-[16px] text-[var(--tvl-ink-3)] tabular-nums"
              style={{ fontFamily: 'var(--tvl-font-mono)' }}
            >
              {approval.campaignSnapshotRef}
            </span>
          </div>
          <Badge variant={badge.variant}>{t(badge.key, badge.fallback)}</Badge>
        </div>

        {approval.status === 'confirmed' && approval.confirmedAt ? (
          <p className="text-[13px] leading-[18px] text-[var(--tvl-ink-2)] tabular-nums">
            {t('com.approvals.approved_at', 'Schváleno / Approved')} {formatDateTime(approval.confirmedAt, locale)}
          </p>
        ) : null}

        <div>
          <Button variant={approval.status === 'awaiting' ? 'primary' : 'secondary'} size="sm" onClick={onOpen}>
            {approval.status === 'awaiting'
              ? t('com.approvals.review', 'Zkontrolovat a schválit / Review & approve')
              : t('com.approvals.view', 'Zobrazit / View')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function ApprovalsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useApprovalsQuery();

  const awaitingCount = (data ?? []).filter((a) => a.status === 'awaiting').length;

  return (
    <PageContainer width="default">
      <header className="flex flex-col gap-1">
        <h1 className="text-[22px] font-semibold leading-[28px] text-[var(--tvl-ink)] md:text-[26px]">
          {t('com.approvals.title', 'Schválení kampaní / Campaign approvals')}
        </h1>
        {!isLoading && !isError ? (
          <p className="text-[14px] leading-[20px] text-[var(--tvl-ink-2)] tabular-nums">
            {t('com.approvals.waiting_count', '{{count}} kampaní čeká / {{count}} awaiting', { count: awaitingCount })}
          </p>
        ) : null}
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : isError ? (
        <EmptyState
          variant="error-loading"
          title={t('com.approvals.error', 'Schválení se nepodařilo načíst / Could not load approvals')}
          action={
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              {t('common.retry', 'Zkusit znovu / Retry')}
            </Button>
          }
        />
      ) : !data || data.length === 0 ? (
        <EmptyState
          variant="empty"
          title={t('com.approvals.empty', 'Žádné kampaně nečekají na schválení / No campaigns awaiting your approval')}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {data.map((a) => (
            <ApprovalCard key={a.id} approval={a} onOpen={() => navigate(`/approvals/${a.campaignId}`)} />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
